import React, { useEffect, useRef, useState } from "react";
import { CornerUpLeft } from "lucide-react";
import {
  formatEmailTimestampToIST,
  resolveEmailBodyFromApi,
} from "Agentic Module/pages/emailTriaging/emailTriageUtils";
import "../../styles/emailInboxUI.css";
import EmailDraftUI from "./emailDraftUI";
import PropTypes from "prop-types";

function normalizeRecipients(raw) {
  if (Array.isArray(raw)) return raw.filter(Boolean).map(String).join(", ");
  if (raw == null) return "";
  return String(raw)
    .split(",")
    .map((v) => v.trim())
    .filter(Boolean)
    .join(", ");
}

function normalizeBody(value) {
  if (value == null) return "";
  return String(value)
    .replaceAll("\r\n", "\n")
    .replaceAll("\r", "\n")
    .replaceAll(String.raw`\r\n`, "\n")
    .replaceAll(String.raw`\n`, "\n")
    .replaceAll(String.raw`\r`, "\n");
}

function isProbablyHtmlString(s) {
  if (!s || typeof s !== "string") return false;
  return /<[a-z][\s\S]*>/i.test(s.trim());
}

function firstRecipientAddress(email) {
  if (!email) return "";
  if (email.email_from) return String(email.email_from);
  const to = email.email_to;
  if (Array.isArray(to) && to.length > 0) return String(to[0]);
  if (typeof to === "string" && to.trim() !== "") {
    return to.split(",")[0].trim();
  }
  return "";
}

function emailBodyPlainText(rawBody) {
  const normalized = normalizeBody(rawBody);
  if (!normalized) return "";

  let plainText = normalized;
  if (isProbablyHtmlString(normalized)) {
    if (typeof window !== "undefined" && window.DOMParser) {
      const doc = new window.DOMParser().parseFromString(normalized, "text/html");
      plainText = doc.body?.textContent ?? "";
    } else {
      plainText = normalized.replace(/<[^<>]+>/g, " ");
    }
  }

  return plainText.replaceAll("\u00a0", " ").trim();
}

function emailBodyPreviewText(rawBody, maxLength = 180) {
  const compact = emailBodyPlainText(rawBody).replace(/\s+/g, " ").trim();
  if (compact.length <= maxLength) return compact;
  return `${compact.slice(0, maxLength)}...`;
}

export default function EmailInboxUI({
  selectedEmail,
  emailFrom,
  emailSent,
  emailModalLoading,
  onSaveDraft,
  onSendEmail,
  onPrepareReply,
  replyTrigger = 0,
  isReplyMode,
  setIsReplyMode,
  snapshotApiRef,
  restoredReply,
  onRestoredReplyApplied,
}) {
  const [isReplyContextExpanded, setIsReplyContextExpanded] = useState(() =>
    Boolean(restoredReply?.isReplyContextExpanded),
  );
  const [replyComposerEmail, setReplyComposerEmail] = useState(
    () => restoredReply?.replyComposerEmail ?? {},
  );
  const [replySyncKey, setReplySyncKey] = useState(0);
  const [replyPreparing, setReplyPreparing] = useState(false);

  const composerRef = useRef(null);
  const lastReplyTrigger = useRef(replyTrigger);
  const replyComposerEmailRef = useRef(replyComposerEmail);
  const isReplyContextExpandedRef = useRef(isReplyContextExpanded);

  replyComposerEmailRef.current = replyComposerEmail;
  isReplyContextExpandedRef.current = isReplyContextExpanded;

  const readViewEmail = selectedEmail ?? {};
  const subject = String(readViewEmail?.email_sub ?? readViewEmail?.mail_subject ?? "(No subject)");
  const from = String(readViewEmail?.email_from ?? readViewEmail?.from ?? "").trim();
  const to = normalizeRecipients(readViewEmail?.email_to ?? readViewEmail?.to);
  const inboxHistoryBody = resolveEmailBodyFromApi(readViewEmail);
  const inboxBodyIsHtml = isProbablyHtmlString(inboxHistoryBody);

  if (snapshotApiRef) {
    snapshotApiRef.current = {
      getSnapshot: () => {
        const data = composerRef.current?.getComposerData?.();
        const replyComposerEmailFromLive = data
          ? {
              email_to: data.emailTo,
              email_cc: data.emailCc,
              email_sub: data.emailSubject,
              email_body: data.emailBody,
            }
          : replyComposerEmailRef.current;
        return {
          replyComposerEmail: replyComposerEmailFromLive ?? {},
          isReplyContextExpanded: Boolean(isReplyContextExpandedRef.current),
        };
      },
    };
  }

  useEffect(() => {
    return () => {
      if (snapshotApiRef) snapshotApiRef.current = null;
    };
  }, [snapshotApiRef]);

  useEffect(() => {
    if (!restoredReply || !isReplyMode) return;
    setReplyComposerEmail(restoredReply.replyComposerEmail ?? {});
    setIsReplyContextExpanded(Boolean(restoredReply.isReplyContextExpanded));
    setReplySyncKey((k) => k + 1);
    onRestoredReplyApplied?.();
    // intentionally omit onRestoredReplyApplied — parent passes an inline setter
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [restoredReply, isReplyMode]);

  const startReplyFlow = async () => {
    if (!selectedEmail || replyPreparing) return;
    setIsReplyMode(true);
    setIsReplyContextExpanded(false);
    if (!onPrepareReply) {
      setReplyComposerEmail({});
      setReplySyncKey((k) => k + 1);
      return;
    }
    setReplyPreparing(true);
    try {
      const template = await onPrepareReply(selectedEmail, false);
      setReplyComposerEmail(template ?? {});
      setReplySyncKey((k) => k + 1);
    } finally {
      setReplyPreparing(false);
    }
  };

  useEffect(() => {
    if (replyTrigger === lastReplyTrigger.current) return;
    lastReplyTrigger.current = replyTrigger;
    if (replyTrigger > 0 && selectedEmail) {
      void startReplyFlow();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [replyTrigger, selectedEmail]);

  useEffect(() => {
    if (!isReplyMode) {
      setIsReplyContextExpanded(false);
    }
  }, [isReplyMode, selectedEmail]);

  const handleCancelReply = () => {
    setIsReplyMode(false);
    setReplyComposerEmail({});
    onRestoredReplyApplied?.();
  };

  const handleSaveDraft = () => {
    const composerData = composerRef.current?.getComposerData?.();
    if (!composerData || !onSaveDraft) return;
    onSaveDraft(composerData, selectedEmail);
  };

  const handleSendEmail = () => {
    const composerData = composerRef.current?.getComposerData?.();
    if (!composerData || !onSendEmail) return;
    onSendEmail(composerData, selectedEmail);
  };

  let inboxHistoryBodyContent;

  if (!inboxHistoryBody) {
    inboxHistoryBodyContent = (
      <div className="email-inbox-card__body email-inbox-card__body--empty">
        No message content.
      </div>
    );
  } else if (inboxBodyIsHtml) {
    inboxHistoryBodyContent = (
      <div
        className="email-inbox-card__body email-inbox-card__body--html"
        // eslint-disable-next-line react/no-danger
        dangerouslySetInnerHTML={{ __html: inboxHistoryBody }}
      />
    );
  } else {
    inboxHistoryBodyContent = (
      <div className="email-inbox-card__body">
        {inboxHistoryBody}
      </div>
    );
  }

  return (
    <>
      {isReplyMode ? (
        <div className="reply-compose-shell">
          <div className="reply-compose-shell__header">
            <div className="reply-compose-shell__title-wrap">
              <span className="reply-compose-shell__icon" aria-hidden>
                <CornerUpLeft size={14} />
              </span>
              <div>
                <h3 className="reply-compose-shell__title">Compose Reply</h3>
              </div>
            </div>
            <div className="reply-compose-shell__actions">
              <button
                type="button"
                onClick={handleCancelReply}
                className="reply-compose-shell__btn reply-compose-shell__btn--cancel"
              >
                Cancel Reply
              </button>
              <button
                type="button"
                onClick={handleSaveDraft}
                disabled={emailModalLoading || replyPreparing}
                className="reply-compose-shell__btn reply-compose-shell__btn--draft"
              >
                Save Draft
              </button>
              <button
                type="button"
                onClick={handleSendEmail}
                disabled={emailSent || emailModalLoading || replyPreparing}
                className="reply-compose-shell__btn reply-compose-shell__btn--send"
              >
                {emailSent ? "Sending..." : "Send Reply"}
              </button>
            </div>
          </div>

          <div className="reply-compose-shell__context-wrap">
            {(() => {
              const replyContextBody = resolveEmailBodyFromApi(selectedEmail ?? {});
              const replyContextBodyIsHtml = isProbablyHtmlString(replyContextBody);
              const replyBodyPlainLength = emailBodyPlainText(replyContextBody)
                .replace(/\s+/g, " ")
                .trim().length;
              const showExpandToggle =
                replyBodyPlainLength > 180 ||
                (replyContextBodyIsHtml && replyContextBody.length > 400);

              const contextBodyClassName =
                !isReplyContextExpanded && showExpandToggle
                  ? " reply-compose-shell__context-body--collapsed"
                  : "";

              const displayedReplyContextBody = isReplyContextExpanded
                ? replyContextBody
                : emailBodyPreviewText(replyContextBody);

              let replyContextContent;

              if (!replyContextBody) {
                replyContextContent = (
                  <div className="reply-compose-shell__context-body reply-compose-shell__context-body--empty">
                    No message content.
                  </div>
                );
              } else if (replyContextBodyIsHtml) {
                replyContextContent = (
                  <div
                    className={`reply-compose-shell__context-body reply-compose-shell__context-body--html${contextBodyClassName}`}
                    // eslint-disable-next-line react/no-danger
                    dangerouslySetInnerHTML={{ __html: replyContextBody }}
                  />
                );
              } else {
                replyContextContent = (
                  <div className="reply-compose-shell__context-body">
                    {displayedReplyContextBody}
                  </div>
                );
              }

              return (
                <>
                  <div className="reply-compose-shell__context-label">Replying to</div>
                  <div className="reply-compose-shell__context-card">
                    <div className="reply-compose-shell__context-meta">
                      <span>
                        {selectedEmail?.email_from
                          ? `From: ${selectedEmail.email_from}`
                          : `To: ${firstRecipientAddress(selectedEmail) || "—"}`}
                      </span>
                      <span>{formatEmailTimestampToIST(selectedEmail?.timestamp)}</span>
                    </div>
                    <div className="reply-compose-shell__context-subject">
                      {selectedEmail?.email_sub ?? selectedEmail?.mail_subject ?? "(No subject)"}
                    </div>
                    {replyContextContent}
                    {showExpandToggle ? (
                      <button
                        type="button"
                        className="reply-compose-shell__context-toggle"
                        onClick={() => setIsReplyContextExpanded((prev) => !prev)}
                      >
                        {isReplyContextExpanded ? "Show less" : "Show more"}
                      </button>
                    ) : null}
                  </div>
                </>
              );
            })()}
          </div>

          <div className="reply-compose-shell__editor">
            <EmailDraftUI
              ref={composerRef}
              initialEmail={replyComposerEmail}
              syncKey={`inbox-reply-${replySyncKey}`}
              emailFrom={emailFrom}
            />
          </div>
        </div>
      ) : (
        <div className="email-inbox-view">
          <div className="email-inbox-card">
            <div className="email-inbox-card__header">
              <div className="email-inbox-card__meta-row">
                <h4 className="email-inbox-card__subject">{subject}</h4>
                <div className="email-inbox-actions">
                  <button
                    type="button"
                    onClick={() => void startReplyFlow()}
                    disabled={emailModalLoading || replyPreparing}
                    className="email-inbox-btn email-inbox-btn--reply"
                  >
                    <CornerUpLeft size={13} />
                    Reply
                  </button>
                </div>
              </div>
              <div>
                <div style={{display: 'flex',gap:'5px',alignItems:'center', fontSize:'12px'}}><span className="email-inbox-card__meta-label">From:</span><div style={{color: '#6b7280',letterSpacing:'.7px'}}>{from || "—"}</div></div>
                <div style={{display: 'flex',gap:'5px',alignItems:'center', fontSize:'12px'}}><span className="email-inbox-card__meta-label">To:</span><div style={{color: '#6b7280',letterSpacing:'.7px'}}>{to || "—"}</div></div>
              </div>

            </div>
            <div className="email-inbox-card__body-wrap">
              {inboxHistoryBodyContent}
            </div>
          </div>


        </div>
      )}
    </>
  );
}

EmailInboxUI.propTypes = {
  selectedEmail: PropTypes.shape({
    email_sub: PropTypes.string,
    mail_subject: PropTypes.string,
    email_from: PropTypes.string,
    from: PropTypes.string,
    email_to: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.array,
    ]),
    to: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.array,
    ]),
    timestamp: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.number,
    ]),
  }),

  emailFrom: PropTypes.string,

  emailSent: PropTypes.bool,

  emailModalLoading: PropTypes.bool,

  onSaveDraft: PropTypes.func,

  onSendEmail: PropTypes.func,

  onPrepareReply: PropTypes.func,

  replyTrigger: PropTypes.number,

  isReplyMode: PropTypes.bool,

  setIsReplyMode: PropTypes.func,

  snapshotApiRef: PropTypes.shape({
    current: PropTypes.shape({
      getSnapshot: PropTypes.func,
    }),
  }),

  restoredReply: PropTypes.shape({
    isReplyContextExpanded: PropTypes.bool,
    replyComposerEmail: PropTypes.object,
  }),

  onRestoredReplyApplied: PropTypes.func,
};
